<template>
  <ModalComponent
    ref="modalRef"
    :submit-button-text="$t('common.close')"
    size="xl"
    @close="$emit('close')"
  >
    <template #title>
      {{ $t("company_orders.transactions.registry_transaction") }}
    </template>

    <template #body>
      <div class="card border-0 shadow-sm mb-3">
        <div class="card-body p-3 p-lg-4">
          <h6 class="text-uppercase text-muted mb-2 fw-semibold section-label">
            {{ $t("company_orders.transactions.registry_transaction") }}
          </h6>
          <div class="mb-3">
            <span class="badge badge-phoenix badge-phoenix-primary">
              {{ formatEventType(event.event_type, t) }}
            </span>
          </div>
          <div class="row g-2 g-lg-3">
            <div
              v-for="field in fields"
              :key="field.label"
              class="col-md-6 col-xl-3"
            >
              <div class="detail-item h-100">
                <div class="text-muted small mb-1">{{ field.label }}</div>
                <div class="fw-semibold text-break">
                  <code v-if="field.code">{{ field.value }}</code>
                  <template v-else>{{ field.value }}</template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card border-0 shadow-sm">
        <div class="card-body p-3 p-lg-4">
          <h6 class="text-uppercase text-muted mb-3 fw-semibold section-label">
            {{ $t("company_orders.associated_portfolio_items") }}
          </h6>
          <LoadingIndicatorComponent v-if="isLoading" />
          <PortfolioItemsTable
            v-else-if="portfolioItems.length > 0"
            :items="portfolioItems"
            :disable-selection="true"
            :enable-count-editing="false"
            storage-context="registry-event"
          />
          <p v-else class="text-muted mb-0">
            {{ $t("company_orders.transactions.no_portfolio_items") }}
          </p>
        </div>
      </div>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import ModalComponent from "@/components/common/ModalComponent.vue";
import { formatDateTime, formatMwh } from "@/composables/formatUtils";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import PortfolioItemsTable from "@/dashboard/inventory_management/common/PortfolioItemsTable.vue";
import { computed, onMounted, ref, shallowRef, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import { transactionLogApi } from "./transactionLogApi";
import type {
  RegistryEventDetail,
  RegistryEventSummary,
} from "./transactionLogTypes";
import {
  formatConnection,
  formatEventType,
} from "./transactionTypePresentation";

/**
 * Shows one registry event. The associated portfolio items are the company's item records on the
 * event's linked certificates, held or not, served with the event detail; the shared portfolio
 * items store is left alone, so it keeps whatever company the dashboard loaded.
 */
const props = defineProps<{
  event: RegistryEventSummary;
}>();

defineEmits<{
  close: [];
}>();

const { t } = useI18n();
const { globalErrorHandler } = useGlobalHandlers();
const modalRef =
  useTemplateRef<InstanceType<typeof ModalComponent>>("modalRef");

const detail = shallowRef<RegistryEventDetail | null>(null);
const isLoading = ref(true);

const portfolioItems = computed(() => detail.value?.portfolio_items ?? []);

const fields = computed(() => {
  const event = props.event;
  const counterparty = event.counterparty;
  return [
    {
      label: t("company_orders.transactions.registry_transaction"),
      value: event.registry_event_id,
      code: true,
    },
    {
      label: t("company_orders.transactions.occurred"),
      value: formatDateTime(event.occurred_at),
    },
    { label: t("common.company"), value: event.company_name },
    {
      label: t("company_orders.portfolio"),
      value: event.allocation?.portfolio_names.join(", "),
    },
    {
      label: t("company_orders.transactions.connection"),
      value: formatConnection(event.connection, t),
    },
    {
      label: t("company_orders.transactions.volume"),
      value: `${formatMwh(event.linked_volume)} MWh`,
    },
    {
      label: t("company_orders.transactions.certificates"),
      value: String(event.linked_certificate_count),
    },
    {
      label: t("company_orders.transactions.volume_in_registry"),
      value: `${formatMwh(event.registry_volume)} MWh`,
    },
    {
      label: t("company_orders.transactions.certificates_in_registry"),
      value: String(event.registry_certificate_count),
    },
    {
      label: t("company_orders.transactions.counterparty"),
      value: counterparty?.name,
    },
    {
      label: t("company_orders.transactions.counterparty_account"),
      value: counterparty?.account_number,
      code: true,
    },
    {
      label: t("company_orders.transactions.counterparty_eecs"),
      value: counterparty?.org_eecs,
      code: true,
    },
  ].filter((field) => field.value);
});

onMounted(async () => {
  if (modalRef.value) modalRef.value.showModal = true;
  try {
    detail.value = (await transactionLogApi.getEvent(props.event.id)).data;
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isLoading.value = false;
  }
});
</script>

<style scoped lang="scss">
@use "./order-details-shared";
</style>
